import { useCallback } from 'react';
import { useWorkspace } from '@/context/WorkspaceContext';
import {
  useAutoRefresh,
  type AutoRefreshContext,
  type AutoRefreshControl,
  type AutoRefreshOptions,
} from './useAutoRefresh';
import { usePollingLoad } from './usePollingLoad';

export type AutoRefreshPollingOptions = {
  hasData: () => boolean;
  load: (context: AutoRefreshContext) => Promise<void>;
  autoRefresh: Omit<AutoRefreshOptions, 'onRefresh'>;
  onWorkspaceChange?: () => void;
};

export type AutoRefreshPollingResult = {
  autoRefresh: AutoRefreshControl;
  initialLoading: boolean;
};

/**
 * Combines useAutoRefresh + usePollingLoad so background polls keep existing UI visible.
 */
export function useAutoRefreshPolling({
  hasData,
  load,
  autoRefresh,
  onWorkspaceChange,
}: AutoRefreshPollingOptions): AutoRefreshPollingResult {
  const { workspaceScopeKey } = useWorkspace();
  const { run, initialLoading } = usePollingLoad({ hasData, onWorkspaceChange });

  const onRefresh = useCallback(
    async (context: AutoRefreshContext) => {
      await run(context.background, () => load(context));
    },
    [load, run],
  );

  const control = useAutoRefresh({
    ...autoRefresh,
    deps: [...(autoRefresh.deps ?? []), workspaceScopeKey],
    onRefresh,
  });

  return { autoRefresh: control, initialLoading };
}
